// The weekly B2C sales report — "التقرير الأسبوعي". Week arithmetic, the rates
// derived from the week's counts, and the labels, shared by the page, the
// endpoint and the tests.
//
// Weeks run Sunday → Saturday, Algeria's working week. Order and event
// timestamps are instants, compared against Algiers midnight; payment and
// session days are stored as UTC-midnight days, compared as days.

import { z } from "zod";

/** "yyyy-mm-dd" `n` days after `day` (negative goes back). */
export function addDays(day: string, n: number) {
	const date = new Date(`${day}T00:00:00Z`);
	date.setUTCDate(date.getUTCDate() + n);
	return date.toISOString().slice(0, 10);
}

/** The Sunday starting the week that holds `day`. */
export function weekStart(day: string) {
	return addDays(day, -new Date(`${day}T00:00:00Z`).getUTCDay());
}

/** The week's seven days, Sunday first. */
export function weekDays(start: string) {
	return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** The instants bounding the week in Algiers time (UTC+1, no DST): [from, to). */
export function weekRange(start: string) {
	return {
		from: new Date(`${start}T00:00:00+01:00`),
		to: new Date(`${addDays(start, 7)}T00:00:00+01:00`),
	};
}

export const isoDaySchema = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "تاريخ غير صالح");

const MONTHS = [
	"جانفي",
	"فيفري",
	"مارس",
	"أفريل",
	"ماي",
	"جوان",
	"جويلية",
	"أوت",
	"سبتمبر",
	"أكتوبر",
	"نوفمبر",
	"ديسمبر",
];

/** "19 أكتوبر 2026" for a "yyyy-mm-dd" day, by hand for the same reason as below. */
export function dayLabel(day: string) {
	const [y, m, d] = day.split("-").map(Number);
	return `${d} ${MONTHS[m - 1]} ${y}`;
}

/**
 * "13 – 19 سبتمبر 2026", or "27 سبتمبر – 3 أكتوبر 2026" across months. Built
 * by hand rather than with Intl so the server and the browser render the same
 * text.
 */
export function weekLabel(start: string) {
	const end = addDays(start, 6);
	const [y1, m1, d1] = start.split("-").map(Number);
	const [y2, m2, d2] = end.split("-").map(Number);
	const first =
		y1 !== y2
			? `${d1} ${MONTHS[m1 - 1]} ${y1}`
			: m1 !== m2
				? `${d1} ${MONTHS[m1 - 1]}`
				: `${d1}`;
	return `${first} – ${d2} ${MONTHS[m2 - 1]} ${y2}`;
}

/* -------------------------------------------------------------------------- */
/*  Figures                                                                   */
/* -------------------------------------------------------------------------- */

export interface WeekCounts {
	/** Orders created during the week. */
	leads: number;
	/** Distinct orders sales contacted: a status change or a WhatsApp message. */
	contacted: number;
	/** Editions with a session day during the week. */
	scheduledEditions: number;
	/** Distinct orders moved to C-02 (confirmed with payment) during the week. */
	paid: number;
	/** Orders confirmed (C-01 / C-02) for the first time this week and still confirmed. */
	confirmed: number;
	/** CA: each of those confirmations at its edition's sale price. */
	revenue: number;
	/** Confirmations left out of `revenue` — their edition has no sale price yet. */
	unpriced: number;
	/** Encaissement: payments dated in the week. */
	collected: number;
	payments: number;
	/** Average paid ÷ expected attendees over the week's editions, as a percent. */
	fillRate: number | null;
}

/** Average fill over the editions whose expected head count is known. */
export function averageFillRate(
	editions: { paid: number; expected: number | null }[],
) {
	const known = editions.filter(
		(e): e is { paid: number; expected: number } =>
			e.expected !== null && e.expected > 0,
	);
	if (known.length === 0) return null;
	return (
		(known.reduce((sum, e) => sum + e.paid / e.expected, 0) / known.length) *
		100
	);
}

export interface WeekRates {
	/** Paid registrations ÷ orders contacted, as a percent. */
	conversion: number | null;
	/** Collected ÷ CA, as a percent. Can pass 100%: a week collects for earlier confirmations too. */
	recovery: number | null;
	/** CA − collected. */
	gap: number;
}

export function weekRates(counts: WeekCounts): WeekRates {
	return {
		conversion:
			counts.contacted > 0 ? (counts.paid / counts.contacted) * 100 : null,
		recovery:
			counts.revenue > 0 ? (counts.collected / counts.revenue) * 100 : null,
		gap: counts.revenue - counts.collected,
	};
}

/** Week-over-week change, or null when either side is unknown. */
export function weekChange(current: number | null, previous: number | null) {
	if (current === null || previous === null) return null;
	return {
		diff: current - previous,
		percent: previous !== 0 ? ((current - previous) / previous) * 100 : null,
	};
}
